import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthPage({ mode = "login" }) {
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isRegister) await register(email, password, name);
      else await login(email, password);
      toast.success(isRegister ? "Account created" : "Welcome back");
      nav("/");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-[#050505] grid-bg">
      <div className="hidden md:flex flex-1 relative overflow-hidden border-r border-zinc-900">
        {/* Alternate hero images (kept in case we want to switch back):
            - https://images.pexels.com/photos/6203470/pexels-photo-6203470.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940
            - https://customer-assets-7cd3h4nn.emergentagent.net/job_trade-journal-402/artifacts/nqqpu489_confluence2.jpg
            - https://customer-assets-7cd3h4nn.emergentagent.net/job_trade-journal-402/artifacts/ffvi9pvd_confnew2.jpg */}
        <div className="absolute inset-0 opacity-90" style={{
          backgroundImage: 'url(https://customer-assets-7cd3h4nn.emergentagent.net/job_trade-journal-402/artifacts/mrrn15ps_conf3-2.jpg)',
          backgroundSize: 'cover', backgroundPosition: 'center -96px',
        }} />
        {/* Fade the bottom of the image into the app background so the
            headline sits on solid black without competing with the graphic. */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
        <div className="relative z-10 p-12 flex flex-col w-full">
          <div className="flex items-center gap-2 text-blue-500">
            <TrendingUp className="w-6 h-6" />
            <span className="font-heading font-semibold text-xl tracking-tight">ZigZagX</span>
          </div>
          <div className="mt-auto">
            <h1 className="font-heading text-4xl lg:text-5xl font-semibold tracking-tight text-zinc-100 max-w-md">
              Trade with<br/>
              <span className="text-blue-500">confluence.</span>
            </h1>
          </div>
          <div className="text-xs text-zinc-600 font-mono mt-6">v1.0 · Live prices via yfinance</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8 flex items-center gap-2 text-blue-500">
            <TrendingUp className="w-6 h-6" />
            <span className="font-heading font-semibold text-lg">ZigZagX</span>
          </div>
          <h2 className="font-heading text-2xl font-semibold mb-1">{isRegister ? "Create account" : "Sign in"}</h2>
          <p className="text-sm text-zinc-500 mb-8">{isRegister ? "Start journaling in seconds." : "Welcome back, trader."}</p>

          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-zinc-500">Name</Label>
                <Input id="name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500">Email</Label>
              <Input id="email" type="email" required data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500">Password</Label>
                {!isRegister && (
                  <Link to="/forgot-password" className="text-[11px] uppercase tracking-wider text-blue-500 hover:text-blue-400" data-testid="auth-forgot-link">
                    Forgot?
                  </Link>
                )}
              </div>
              <PasswordInput id="password" required data-testid="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" />
            </div>
            <Button type="submit" disabled={busy} data-testid="auth-submit-btn" className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 text-white h-10">
              {busy ? "…" : isRegister ? "Create account" : "Sign in"}
            </Button>
            <p className="text-[11px] text-zinc-500 text-center leading-relaxed" data-testid="auth-tos-notice">
              By continuing, you agree to our{" "}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 underline" data-testid="auth-tos-link">
                Terms of Service
              </Link>
              {" "}and{" "}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 underline" data-testid="auth-privacy-link">
                Privacy Policy
              </Link>
              .
            </p>
          </form>

          {isRegister && (
            <div className="mt-6 text-sm text-zinc-500">
              Already have an account? <Link to="/login" className="text-blue-500 hover:text-blue-400" data-testid="auth-switch-login">Sign in</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
